import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

export default function SessionsLayout() {
  const location = useLocation();

  useEffect(() => {
    // Override _globals.scss 'body { overflow-y: scroll; }' to remove empty scrollbar track
    const originalHtmlOverflow = document.documentElement.style.overflowY;
    const originalBodyOverflow = document.body.style.overflowY;
    document.documentElement.style.overflowY = "auto";
    document.body.style.overflowY = "auto";

    return () => {
      document.documentElement.style.overflowY = originalHtmlOverflow;
      document.body.style.overflowY = originalBodyOverflow;
    };
  }, []);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#08070a",
      }}
    >
      {/* ── Fixed Background Mosaic Image ── */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundImage: "url('/assets/Groupe 20166.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: 0,
        }}
      />

      {/* ── Centered Card Shell ── */}
      <div
        className="auth-shell"
        style={{
          position: "relative",
          zIndex: 2,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <AnimatePresence mode="wait">
          <Outlet key={location.pathname} />
        </AnimatePresence>
      </div>

      <style>{`
        html, body {
          overflow-y: auto !important;
        }

        .auth-shell {
          padding: clamp(20px, 3vw, 44px);
        }
        @media (max-width: 480px) {
          .auth-shell { padding: 14px; }
        }

        /* ── Sleek Compact Card Width (760px) ── */
        .auth-card {
          width: 100%;
          max-width: 760px;
          height: auto;
          min-height: 580px;
          display: grid;
          grid-template-columns: 45% 55%;
          border-radius: 20px;
          border: 2px solid rgba(255, 255, 255, 0.9);
          overflow: hidden;
          background-color: #ffffff;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65), 0 4px 16px rgba(0, 0, 0, 0.3);
        }

        /* ── Left Panel with Video Background ── */
        .auth-left {
          width: 100%;
          min-width: 0;
          min-height: 0;
          padding: 30px 32px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
          position: relative;
          box-sizing: border-box;
          background: #0d0a11;
          color: #fff;
        }

        .auth-left-bg-video {
          position: absolute;
          inset: -4%;
          width: 108%;
          height: 108%;
          object-fit: cover;
          object-position: center;
          transform: scale(1.06);
          transform-origin: center;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.5s ease;
          z-index: 0;
          pointer-events: none;
        }

        .auth-left-bg-video.active {
          opacity: 1;
          visibility: visible;
        }

        .auth-left-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.35) 0%,
            rgba(0, 0, 0, 0.02) 40%,
            rgba(0, 0, 0, 0.75) 100%
          );
          z-index: 1;
          pointer-events: none;
        }

        .auth-logo-row {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
        }

        .auth-logo {
          height: 30px;
          width: auto;
          display: block;
          object-fit: contain;
        }

        .auth-left-bottom {
          position: relative;
          z-index: 2;
        }

        .auth-indicators {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 14px;
        }

        .auth-indicator-bar {
          height: 3.5px;
          border-radius: 2px;
          border: none;
          outline: none;
          cursor: pointer;
          padding: 0;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.35);
          width: 16px;
        }

        .auth-indicator-bar.active {
          width: 32px;
          background: #ffffff;
        }

        .auth-slide-title {
          color: #ffffff;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.25;
          margin: 0 0 6px 0;
          letter-spacing: -0.2px;
        }

        .auth-slide-desc {
          color: rgba(255, 255, 255, 0.8);
          font-size: 12.5px;
          line-height: 1.5;
          margin: 0;
          max-width: 260px;
        }

        /* ── Right Panel ── */
        .auth-right {
          width: 100%;
          min-width: 0;
          min-height: 0;
          background: #ffffff;
          padding: 30px 34px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          box-sizing: border-box;
        }

        .auth-right-top {
          width: 100%;
          max-width: 320px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
          flex-shrink: 0;
        }

        .auth-right-body {
          flex: 1;
          width: 100%;
          max-width: 320px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 0;
        }

        .lang-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 500;
          color: #4B5563;
          background: #F3F4F6;
          border: 1px solid #E5E7EB;
          border-radius: 999px;
          padding: 3px 10px;
          cursor: pointer;
          text-decoration: none;
        }

        .signup-hint {
          font-size: 12px;
          color: #6B7280;
          text-align: right;
          margin-left: auto;
          white-space: nowrap;
        }

        .signup-hint a {
          color: #F95F1F;
          font-weight: 600;
          text-decoration: none;
        }

        .signup-hint a:hover {
          color: #E42E45;
          text-decoration: underline;
        }

        .auth-title {
          font-size: 26px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 4px 0;
          flex-shrink: 0;
          letter-spacing: -0.3px;
        }

        .auth-subtitle {
          font-size: 12.5px;
          line-height: 1.45;
          color: #6B7280;
          margin-bottom: 18px;
          flex-shrink: 0;
        }

        .field-group {
          margin-bottom: 12px;
        }

        .field-label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 5px;
        }

        .field-label,
        .field-group label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: #4B5563;
          margin-bottom: 5px;
        }

        .forgot-link {
          font-size: 11.5px;
          color: #F95F1F;
          font-weight: 500;
          text-decoration: none;
        }

        .forgot-link:hover {
          color: #E42E45;
          text-decoration: underline;
        }

        .input-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }

        .auth-input {
          width: 100%;
          box-sizing: border-box;
          background: #FAFAFA;
          border: 1px solid #E5E7EB;
          color: #111827;
          border-radius: 7px;
          height: 40px;
          padding: 0 12px;
          font-size: 13px;
          transition: all 0.2s ease;
        }

        .auth-input:focus {
          outline: none;
          background: #ffffff;
          border-color: #111827;
          box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.08);
        }

        .password-wrap {
          position: relative;
        }

        .password-wrap .auth-input {
          padding-right: 38px;
        }

        .eye-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #9CA3AF;
          display: flex;
          align-items: center;
        }

        .field-error {
          color: #DC2626;
          font-size: 11px;
          margin-top: 3px;
          line-height: 1.2;
        }

        .btn-submit {
          width: 100%;
          border: none;
          background-color: #0A0A0A;
          color: #ffffff;
          border-radius: 7px;
          height: 42px;
          padding: 0 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          margin-top: 16px;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.2s ease;
          flex-shrink: 0;
        }

        .btn-submit:hover {
          background-color: #1F2937;
          transform: translateY(-1px);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .terms-copy {
          text-align: center;
          font-size: 11.5px;
          color: #9CA3AF;
          margin-top: 18px;
          flex-shrink: 0;
          line-height: 1.5;
        }

        .terms-copy div {
          display: block;
        }

        .terms-copy a {
          color: #6B7280;
          text-decoration: underline;
        }

        .terms-copy a:hover {
          color: var(--theme-primary, #f95f1f);
        }

        .back-icon-btn {
          width: 30px;
          height: 30px;
          border: 1px solid #E5E7EB;
          color: #111827;
          background: #fff;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          cursor: pointer;
          margin-bottom: 12px;
          flex-shrink: 0;
          transition: background 0.2s ease;
        }

        .back-icon-btn:hover {
          background: #F9FAFB;
        }

        .auth-info-banner {
          background-color: #DCFCE7;
          border: 1px solid #BBF7D0;
          color: #166534;
          padding: 9px 12px;
          border-radius: 7px;
          font-size: 11.5px;
          line-height: 1.4;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-top: 14px;
          text-align: left;
        }

        /* ── Responsive Rules ── */
        @media (max-width: 860px) {
          .auth-card {
            grid-template-columns: 1fr;
            max-width: 440px;
            min-height: auto;
          }
          .auth-left {
            padding: 22px 22px;
            min-height: 260px;
          }
          .auth-right {
            padding: 26px 22px;
            overflow-y: auto;
          }
          .auth-title {
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .auth-card {
            border-radius: 16px;
            border-width: 1px;
            min-height: auto;
          }
          .auth-left {
            padding: 18px 18px;
            min-height: 200px;
          }
          .auth-right {
            padding: 22px 18px;
          }
          .auth-right-top,
          .auth-right-body {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
